import { capMatchPercent } from "./matchScoreUtils";
import { getCurrentProductAiRank } from "./resolveSimilarProducts";

export const PRODUCT_MATCH_SOURCES = {
  AI_SESSION: "ai_session",
  SHOPPER_MODEL: "shopper_model",
  CLIENT_WIZARD: "client_wizard",
};

const getProductId = (product) => {
  if (!product) return null;
  return String(product._id || product.id);
};

/** Product entry from the active home AI session, if any. */
export function getAiSessionProductEntry(product, aiSession) {
  if (!product || !aiSession?.products?.length) return null;
  const currentId = getProductId(product);
  return (
    aiSession.products.find((p) => getProductId(p) === currentId) || null
  );
}

export function searchParamsToWizardAnswers(searchParams) {
  if (!searchParams || typeof searchParams !== "object") return null;

  const budget = searchParams.budget;
  const hasBudget =
    budget &&
    ((budget.min != null && budget.min !== "") ||
      (budget.max != null && budget.max !== ""));

  return {
    type: searchParams.category || undefined,
    budget: hasBudget ? budget : undefined,
    mustHaves: searchParams.mustHaves || [],
    niceToHaves: searchParams.niceToHaves || [],
    useCase: searchParams.useCase || undefined,
  };
}

const buildBudgetReason = (budget, score) => {
  if (!budget) return null;
  const min = budget.min ?? 0;
  const max = budget.max ?? null;
  if (max != null && min > 0) {
    return score >= 90
      ? `Fits your $${min.toLocaleString()}-$${max.toLocaleString()} budget`
      : `Aligned with your $${min.toLocaleString()}-$${max.toLocaleString()} budget`;
  }
  if (max != null) {
    return `Within your search budget (up to $${max.toLocaleString()})`;
  }
  return null;
};

/**
 * Canonical match payload when the product is in the active AI session.
 * Used by PDP hero, cards, and deeper insights.
 */
export function buildAiSessionMatchPayload(product, aiSession) {
  const entry = getAiSessionProductEntry(product, aiSession);
  if (!entry) return null;

  const rawScore = Number(entry.matchScore ?? entry.score ?? entry.match ?? null);
  if (!Number.isFinite(rawScore)) return null;
  const score = capMatchPercent(rawScore);

  const explanation =
    typeof entry.matchExplanation === "string"
      ? entry.matchExplanation.trim()
      : "";
  const budget = aiSession?.searchParams?.budget;
  const budgetReason = buildBudgetReason(budget, score);

  const reasons = [];
  if (explanation) {
    reasons.push(
      explanation.length > 220 ? `${explanation.slice(0, 217)}…` : explanation,
    );
  }
  if (budgetReason && !reasons.includes(budgetReason)) {
    reasons.push(budgetReason);
  }

  const dimensionsList = [
    {
      name: "AI Match",
      score,
      reason: explanation || "Matched from your AI search",
      icon: "FaCheckCircle",
      key: "ai_match",
    },
  ];

  if (budgetReason) {
    dimensionsList.push({
      name: "Budget Fit",
      score,
      reason: budgetReason,
      icon: "FaDollarSign",
      key: "budget",
    });
  }

  const rank = getCurrentProductAiRank(product, aiSession);

  return {
    source: PRODUCT_MATCH_SOURCES.AI_SESSION,
    score,
    rank,
    overallScore: score,
    dimensionsList,
    intentSnapshot: aiSession?.searchParams || null,
    dealBreakerFailed: false,
    matchInsights: {
      percentage: score,
      dimensions: dimensionsList,
      reasons: reasons.slice(0, 3),
      intentSnapshot: aiSession?.searchParams || null,
      dealBreakerFailed: false,
    },
    wizardAnswers: searchParamsToWizardAnswers(aiSession?.searchParams),
  };
}

/** Attach aiRanked score fields when product is in the active AI session. */
export function enrichProductWithAiSessionMatch(product, aiSession) {
  if (!product) return product;
  const payload = buildAiSessionMatchPayload(product, aiSession);
  if (!payload) return product;

  return {
    ...product,
    matchScore: payload.score,
    aiRanked: true,
    rank: payload.rank ?? product.rank,
    matchExplanation:
      getAiSessionProductEntry(product, aiSession)?.matchExplanation ||
      product.matchExplanation,
  };
}

export default buildAiSessionMatchPayload;
